/*
 *  Copyright IBM Corp. 2025, 2026
 *
 *  This source code is licensed under the Apache-2.0 license found in the
 *  LICENSE file in the root directory of this source tree.
 *
 *  @license
 */

import ArrowRight16 from "@carbon/icons/es/arrow--right/16.js";
import { carbonIconToReact } from "../../../utils/carbonIcon";
import React from "react";
import { useIntl } from "../../../hooks/useIntl";
import { useSelector } from "../../../hooks/useSelector";

import { useServiceManager } from "../../../hooks/useServiceManager";
import actions from "../../../store/actions";
import { AppState } from "../../../../types/state/AppState";
import { getURLHostName } from "../../../utils/browserUtils";
import VisuallyHidden from "../../../components/util/VisuallyHidden";
import { ClickableImage } from "../util/ClickableImage";
import { IFrameItem } from "../../../../types/messaging/Messages";

interface IFramePreviewCardComponentProps {
  /**
   * The iframe response type item.
   */
  messageItem: IFrameItem;
}

/**
 * The iframe preview card for the page source. This is a button that can be clicked in order to open the frame panel.
 */
function IFramePreviewCardComponent({
  messageItem,
}: IFramePreviewCardComponentProps) {
  const { source, image_url, title, description } = messageItem;
  const aiEnabled = useSelector(
    (state: AppState) => state.config.derived.themeWithDefaults.aiEnabled,
  );
  const urlHostName = getURLHostName(source);
  const { store } = useServiceManager();
  const iframe_ariaImageAltText = useSelector(
    (state: AppState) => state.languagePack.iframe_ariaImageAltText,
  );
  const { formatMessage } = useIntl();
  const iframeAriaClickPreviewCardMessage = formatMessage(
    { id: "iframe_ariaClickPreviewCard" },
    { source: urlHostName },
  );
  const ArrowRight = carbonIconToReact(ArrowRight16);

  /**
   * Set iframe content to be loaded in the iframe panel.
   */
  function handleCardClick() {
    // If tanya has authored an iframe response type and has provided no page source, we don't want the preview card to
    // open the iframe panel.
    if (source) {
      store.dispatch(actions.setIFrameContent(messageItem));
    }
  }

  return (
    <div>
      <ClickableImage
        title={title}
        description={description}
        source={image_url}
        displayURL={source}
        altText={iframe_ariaImageAltText}
        renderIcon={ArrowRight}
        onClick={handleCardClick}
        preventInlineError
        useAITheme={aiEnabled}
      />
      <VisuallyHidden>{iframeAriaClickPreviewCardMessage}</VisuallyHidden>
    </div>
  );
}

const IFramePreviewCardExport = React.memo(IFramePreviewCardComponent);

export { IFramePreviewCardExport as IFramePreviewCard };
